import { EditIcon } from '@chakra-ui/icons'
import { Box, Button, Collapse, Flex, HStack, IconButton, Text, VStack } from '@chakra-ui/react'
import {
  PopUpMessageContext,
  useAccessLevelField,
  useApp,
  useCachedUser,
  useCachedUserItem,
} from '@hb/react'
import {
  DropdownField,
  FieldTypes,
  FileDBValue,
  FileField,
  objectToArray,
  ThreadType,
  UserGroup,
  UserRoleItem,
} from '@hb/shared'
import { useCallback, useContext, useMemo, useState } from 'react'
import {
  assignThreadFileAsConsentForm,
  copyChatFileToAssessment,
  copyChatFileToPatient,
} from '../../backend/functions/other'
import { useProfile } from '../../contexts/user/ProfileContext'
import { useConsentFormsStatus } from '../../hooks'
import { useUserAssessments } from '../../hooks/backend/user/useUserAssessments'
import { CollapseError } from '../CollapseError'
import { FileView, StandaloneInput } from '../forms/Input'
import { LoadingView } from '../LoadingOverlay'
import { DefaultModal } from '../Modals/DefaultModal'
import { SearchedPatientPreview, SearchUsers } from '../Users/SearchUsers'
import { SearchAssessments } from './SearchAssessments'

const copiedFileField: FileField = {
  type: FieldTypes.FILE,
  name: '',
  placeholder: 'Copied File',
}

const PatientSelectedView = ({
  onRemove,
  patient,
  patientLoading,
}: {
  onRemove: () => void
  patient: UserRoleItem | null
  patientLoading: boolean
}) => (
  <Flex w="100%" borderBottom="1px solid #cdcdcd" justify="space-between" pb={1}>
    <SearchedPatientPreview patient={patient} loading={patientLoading} />
    <IconButton
      aria-label="Remove user selection"
      icon={<EditIcon />}
      variant="ghost"
      size="sm"
      onClick={onRemove}
      borderRadius="full"
    />
  </Flex>
)

export const CopyChatFileToProfileModal = ({
  onClose,
  file,
  threadId,
  threadType,
  messageGroupId,
  messageId,
}: {
  onClose: () => void
  file: FileDBValue
  threadId: string
  threadType: ThreadType
  messageGroupId?: string
  messageId?: string
}) => {
  const { processResponse } = useContext(PopUpMessageContext)
  const [submitting, setSubmitting] = useState(false)

  const { assessmentId: contextAssessmentId, patientRef } = useProfile()

  const { appName } = useApp()
  const [patientId, setPatientId] = useState<string | null>(patientRef?.id ?? null)
  const [error, setError] = useState<string | null>(null)

  // Copy destination: 'patient' | 'assessment' | 'consent-form'
  const [copyDestination, setCopyDestination] = useState<
    'patient' | 'assessment' | 'consent-form' | null
  >(null)
  const [assessmentId, setAssessmentId] = useState<string | null>(contextAssessmentId ?? null)
  const [consentFormId, setConsentFormId] = useState<string | null>(null)

  const [accessLevel, setAccessLevel] = useState<UserGroup | null>(
    appName === 'providers-app' ? 'practice' : null,
  )

  // Get patient data to check for consent forms and assessments
  const { data: roleItem, loading: roleItemLoading } = useCachedUser(patientId)
  const { data: userItem, loading: userItemLoading } = useCachedUserItem(
    !!roleItem?.isInvite,
    roleItem && patientId ? patientId : null,
  )
  const { consentForms } = useConsentFormsStatus(patientId, userItem, null)
  const { data: assessments } = useUserAssessments(patientId, false)

  const unsignedConsentForms = useMemo(
    () => objectToArray(consentForms ?? {}).filter(form => !form.signedOn),
    [consentForms],
  )

  const assessmentsArray = useMemo(() => objectToArray(assessments ?? {}), [assessments])

  const hasUnsignedConsentForms = unsignedConsentForms.length > 0 && !!messageGroupId && !!messageId
  const hasAssessments = assessmentsArray.length > 0
  const showDestinationSelector = hasUnsignedConsentForms || hasAssessments

  const accessLevelField = useAccessLevelField(undefined, copyDestination === 'patient')

  // Consent form dropdown field
  const consentFormField = useMemo<DropdownField | null>(
    () =>
      unsignedConsentForms.length && file.type === 'application/pdf'
        ? {
            type: FieldTypes.DROPDOWN,
            placeholder: 'Select the consent form to assign this file to',
            options: unsignedConsentForms.map(form => ({
              text: form.name,
              id: form.id,
            })),
          }
        : null,
    [unsignedConsentForms, file],
  )

  // Copy destination dropdown field
  const copyDestinationField = useMemo<DropdownField | null>(
    () =>
      showDestinationSelector
        ? {
            type: FieldTypes.DROPDOWN,
            placeholder: 'Where would you like to copy this file?',
            options: [
              { text: 'Patient Profile', id: 'patient' },
              ...(hasAssessments ? [{ text: 'Assessment', id: 'assessment' }] : []),
              ...(consentFormField ? [{ text: 'Consent Form', id: 'consent-form' }] : []),
            ],
          }
        : null,
    [showDestinationSelector, hasAssessments, consentFormField],
  )

  const canSelectAccessLevel = useMemo(() => {
    if (!patientId) return false
    const usedCopyDestination = showDestinationSelector ? copyDestination : 'patient'
    if (usedCopyDestination === 'assessment' && !assessmentId) return false
    return usedCopyDestination !== 'consent-form'
  }, [showDestinationSelector, copyDestination, assessmentId, patientId])

  const canSubmit = useMemo(() => {
    if (!patientId) return false
    if (showDestinationSelector && !copyDestination) return false
    const usedCopyDestination = showDestinationSelector ? copyDestination : 'patient'
    if (usedCopyDestination === 'assessment' && !assessmentId) return false
    if (usedCopyDestination === 'consent-form' && !consentFormId) return false
    if (!accessLevel && usedCopyDestination !== 'consent-form') return false
    return true
  }, [
    patientId,
    showDestinationSelector,
    copyDestination,
    assessmentId,
    consentFormId,
    accessLevel,
  ])

  const loadingUser = useMemo(
    () => roleItemLoading || userItemLoading,
    [roleItemLoading, userItemLoading],
  )

  const onSubmit = useCallback(async () => {
    if (!patientId) return processResponse({ error: 'Please select a patient' })

    // Determine destination
    const destination = showDestinationSelector ? copyDestination : 'patient'

    if (destination === 'consent-form') {
      if (!consentFormId) return processResponse({ error: 'Please select a consent form' })
      if (!messageGroupId || !messageId) {
        return processResponse({
          error: 'Message information is required to assign as consent form',
        })
      }
      setSubmitting(true)
      setError(null)
      try {
        await assignThreadFileAsConsentForm({
          patientId,
          file,
          messageGroupId,
          messageId,
          consentFormId,
          threadId,
          threadType,
        })
        setSubmitting(false)
        onClose()
        return processResponse({ success: 'File assigned as consent form successfully' })
      } catch (err: any) {
        setSubmitting(false)
        setError(err?.message || 'Error occurred while assigning file as consent form')
        return processResponse({
          error: err?.message || 'Error occurred while assigning file as consent form',
        })
      }
    } else if (destination === 'assessment') {
      if (!assessmentId) return processResponse({ error: 'Please select an assessment' })
      if (!accessLevel) return processResponse({ error: 'Please select an access level' })
      setSubmitting(true)
      setError(null)
      try {
        await copyChatFileToAssessment({
          accessLevel,
          assessmentId,
          threadType,
          file,
          threadId,
        })
        setSubmitting(false)
        onClose()
        return processResponse({ success: 'File copied to assessment successfully' })
      } catch (err: any) {
        setSubmitting(false)
        setError(err?.message || 'Error occurred while copying file to assessment')
        return processResponse({
          error: err?.message || 'Error occurred while copying file to assessment',
        })
      }
    } else {
      // Copy to patient profile
      if (!accessLevel) return processResponse({ error: 'Please select an access level' })
      setSubmitting(true)
      setError(null)
      try {
        await copyChatFileToPatient({
          accessLevel,
          patientId,
          threadId,
          threadType,
          file,
        })
        setSubmitting(false)
        onClose()
        return processResponse({ success: 'File copied to patient profile successfully' })
      } catch (err: any) {
        setSubmitting(false)
        setError(err?.message || 'Error occurred while copying file to patient profile')
        return processResponse({
          error: err?.message || 'Error occurred while copying file to patient profile',
        })
      }
    }
  }, [
    patientId,
    showDestinationSelector,
    copyDestination,
    consentFormId,
    messageGroupId,
    messageId,
    assessmentId,
    accessLevel,
    processResponse,
    threadId,
    threadType,
    file,
    onClose,
  ])
  return (
    <DefaultModal
      isOpen={true}
      overlayHeader
      onClose={onClose}
      headerProps={{
        onClick: e => {
          e.stopPropagation()
        },
      }}
      size="xl"
      scrollBehavior="inside"
      render={() => (
        <VStack align="flex-start" w="100%" bg="gray.50" p={3}>
          <Text fontFamily="Hero-New" fontWeight={500} color="gray.600" px={2}>
            Copy File to Profile
          </Text>
          <Box w="100%" border="1px solid #cdcdcd" bg="white" overflow="hidden" borderRadius={6}>
            <FileView noFullView field={copiedFileField} data={file} />
          </Box>
          <Box w="100%">
            {roleItem && userItem ? (
              <PatientSelectedView
                patient={roleItem}
                patientLoading={roleItemLoading}
                onRemove={() => {
                  setPatientId(null)
                  setCopyDestination(null)
                  setAssessmentId(null)
                  setConsentFormId(null)
                  setAccessLevel(null)
                }}
              />
            ) : (
              <SearchUsers userTypeName="patient" onSelect={id => setPatientId(id)} />
            )}
          </Box>
          <Collapse in={loadingUser}>
            <LoadingView text="Loading patient information" />
          </Collapse>
          <VStack px={2} w="100%">
            {/* Show message if patient has neither consent forms nor assessments */}
            <Collapse
              style={{ width: '100%' }}
              in={!!patientId && !showDestinationSelector && !loadingUser}>
              <Box w="100%" px={2} py={2}>
                <Text color="gray.600">
                  This file will be copied to the patient profile because the patient has no
                  unsigned consent forms or assessments.
                </Text>
              </Box>
            </Collapse>

            {/* Show destination selector if patient has consent forms or assessments */}
            <Collapse
              style={{ width: '100%' }}
              in={!!patientId && showDestinationSelector && !loadingUser}>
              <Box p={1} w="100%">
                <Text mb={1} color="gray.600" fontFamily="Hero-New" fontWeight={500}>
                  Copy file to:
                </Text>
                {copyDestinationField ? (
                  <StandaloneInput
                    theme="detailed"
                    field={copyDestinationField}
                    value={copyDestination}
                    onChange={value => {
                      setCopyDestination(value as 'patient' | 'assessment' | 'consent-form')
                      // Reset dependent selections
                      if (value !== 'assessment') setAssessmentId(null)
                      if (value !== 'consent-form') setConsentFormId(null)
                      setAccessLevel(null)
                    }}
                  />
                ) : null}
              </Box>
            </Collapse>

            {/* Show assessment selector when assessment is selected */}
            <Collapse
              style={{ width: '100%' }}
              in={!!patientId && copyDestination === 'assessment' && !loadingUser}>
              <Box w="100%">
                <SearchAssessments
                  noPatientPreview
                  onBack={() => setCopyDestination(null)}
                  assessmentId={assessmentId}
                  patientId={patientId}
                  onSubmit={async (id, pId) => {
                    setAssessmentId(id)
                    if (pId) setPatientId(pId)
                  }}
                />
              </Box>
            </Collapse>

            {/* Show consent form selector when consent form is selected */}
            <Collapse
              style={{ width: '100%' }}
              in={!!patientId && copyDestination === 'consent-form' && !loadingUser}>
              <Box p={1} w="100%">
                {consentFormField ? (
                  <StandaloneInput
                    theme="detailed"
                    field={consentFormField}
                    value={consentFormId}
                    onChange={value => setConsentFormId(value)}
                  />
                ) : null}
              </Box>
            </Collapse>

            {/* Show access level selector */}
            <Collapse style={{ width: '100%' }} in={canSelectAccessLevel && !loadingUser}>
              <Box p={1} w="100%">
                {accessLevelField ? (
                  <StandaloneInput
                    theme="detailed"
                    field={accessLevelField}
                    value={accessLevel}
                    onChange={value => setAccessLevel(value)}
                  />
                ) : null}
              </Box>
            </Collapse>
          </VStack>
          <CollapseError error={error} />
          <HStack justify="flex-end" w="100%">
            <Button
              onClick={onClose}
              isDisabled={submitting}
              size="sm"
              bg="white"
              border="1px solid #cdcdcd"
              fontWeight={500}
              fontFamily="Open Sans">
              Cancel
            </Button>
            <Button
              onClick={onSubmit}
              isLoading={submitting}
              size="sm"
              isDisabled={!canSubmit}
              _hover={{ bg: 'green.300' }}
              fontWeight={500}
              fontFamily="Open Sans"
              colorScheme="green">
              Copy File
            </Button>
          </HStack>
        </VStack>
      )}
    />
  )
}
